import React, { useState } from 'react';
import { View, StyleSheet, Image, Pressable, TextInput, KeyboardAvoidingView, Platform, ScrollView } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation, useRoute } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { ProfileData } from '../../services/discoveryOnboardingAPI';
import { 
  Text, 
  Theme,
  WhatsAppVoiceInput,
  AppHeader,
  OnboardingPageContainer
} from '../../components/ui';

interface NameInputScreenProps {
  route: {
    params: {
      profile: ProfileData;
    };
  };
}

const NameInputScreen = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const { t } = useTranslation();
  const { profile } = route.params as NameInputScreenProps['route']['params'];
  const [name, setName] = useState('');

  const handleContinue = () => {
    if (name.trim()) {
      // Navigate to registration with profile data
      (navigation as any).navigate('Auth', { 
        screen: 'Register',
        params: {
          profile,
          userName: name.trim()
        }
      });
    }
  };

  const handleVoiceTranscription = (transcribedText: string) => {
    // Replace the current text with the transcribed text for name input
    setName(transcribedText);
  };

  const canContinue = name.trim().length > 0;

  return (
    <SafeAreaView style={styles.container}>
      <OnboardingPageContainer>
        <KeyboardAvoidingView 
          style={styles.content}
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
          keyboardVerticalOffset={Platform.OS === 'ios' ? 100 : 0}
          enabled={true}
        >
          <ScrollView 
            style={styles.scrollContainer}
            contentContainerStyle={styles.scrollContent}
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator={false}
          >
            <AppHeader showBackButton />
            
            <View style={styles.mainContent}>
              {/* Avatar Image */}
              <Image 
                style={styles.avatarImage} 
                resizeMode="cover" 
                source={require('../../assets/mentor/mentor-welcome.webp')} 
              />
              
              {/* Header Text */}
              <View style={styles.headerContainer}>
                <Text variant="heading2" style={styles.title}>
                  {t('nameInputScreen.greeting1')}
                </Text>
                <Text variant="heading2" style={styles.title}>
                  {t('nameInputScreen.greeting2')}
                </Text>
              </View>
            </View>
          </ScrollView>
          
          {/* Input Field */}
          <View style={styles.inputContainer}>
            <View style={styles.inputFieldWrapper}>
              <TextInput
                style={[
                  styles.inputField,
                  styles.inputText,
                  name ? styles.inputTextFilled : styles.placeholder
                ]}
                value={name}
                onChangeText={setName}
                placeholder={t('nameInputScreen.placeholder')}
                placeholderTextColor={Theme.colors.textDisabled}
                autoFocus
                returnKeyType="done"
                onSubmitEditing={handleContinue}
                multiline={false}
                textAlignVertical="center"
              />
            </View>
            
            <View style={styles.buttonContainer}>
              <WhatsAppVoiceInput
                onTranscription={handleVoiceTranscription}
                disabled={false}
              />
              <Pressable 
                style={[
                  styles.sendButton,
                  canContinue && styles.sendButtonActive
                ]}
                onPress={handleContinue}
                disabled={!canContinue}
              >
                <Ionicons 
                  name="send" 
                  size={18} 
                  color={canContinue ? Theme.colors.textInverse : Theme.colors.textSecondary} 
                />
              </Pressable>
            </View>
          </View>
        </KeyboardAvoidingView>
      </OnboardingPageContainer>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Theme.colors.background,
  },
  content: {
    flex: 1,
  },
  scrollContainer: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  mainContent: {
    flex: 1,
    padding: Theme.spacing.xxl,
    gap: Theme.spacing.xxl,
    alignItems: 'center',
  },
  avatarImage: {
    width: 154,
    height: 231,
  },
  headerContainer: {
    gap: Theme.spacing.sm,
    alignSelf: 'stretch',
  },
  title: {
    color: Theme.colors.textPrimary,
    lineHeight: 32,
    fontSize: 24,
    textAlign: 'left',
    fontFamily: Theme.typography.fontFamily.regular,
    alignSelf: 'stretch',
  },
  inputContainer: {
    flexDirection: 'row',
    paddingVertical: Theme.spacing.sm,
    paddingHorizontal: Theme.spacing.lg,
    gap: Theme.spacing.sm,
    alignSelf: 'stretch',
  },
  inputFieldWrapper: {
    flex: 1,
  },
  inputField: {
    borderRadius: Theme.borderRadius.md,
    backgroundColor: Theme.colors.surfaceSecondary,
    height: 80,
    paddingHorizontal: Theme.spacing.md,
    paddingVertical: Theme.spacing.sm,
    textAlignVertical: 'center',
  },
  inputText: {
    fontSize: 16,
    lineHeight: 26,
    flex: 1,
  },
  inputTextFilled: {
    fontFamily: Theme.typography.fontFamily.regular,
    color: Theme.colors.textPrimary,
  },
  placeholder: {
    fontFamily: Theme.typography.fontFamily.regular,
    color: Theme.colors.textDisabled,
  },
  buttonContainer: {
    gap: Theme.spacing.sm,
  },
  sendButton: {
    width: 36,
    height: 36,
    borderRadius: Theme.borderRadius.sm,
    backgroundColor: Theme.colors.disabled,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sendButtonActive: {
    backgroundColor: Theme.colors.primary,
  },
});

export default NameInputScreen; 